<script>
import axios from "axios";
import appConfig from "@/app.config";
import {notifyModel} from "@/models/notifyModel";

const TYPES = [
  {value: "VAN_BAN_DI", label: "Sổ công văn đi", icon: "mdi-email-send-outline"},
  {value: "VAN_BAN_DEN", label: "Sổ văn bản đến", icon: "mdi-email-receive-outline"},
];

const GRANULARITIES = [
  {value: "DAY", label: "Theo ngày"},
  {value: "WEEK", label: "Theo tuần"},
  {value: "MONTH", label: "Theo tháng"},
  {value: "QUARTER", label: "Theo quý"},
  {value: "YEAR", label: "Theo năm"},
  {value: "CUSTOM", label: "Tùy chọn khoảng thời gian"},
];

const TRANG_THAI_OPTIONS = [
  {value: "", label: "Tất cả"},
  {value: "BAN_HANH", label: "Đã ban hành"},
  {value: "DA_DONG_MOC", label: "Đã đóng mộc"},
  {value: "DUYET", label: "Đã duyệt"},
];

const STATUS_VARIANTS = {
  QUEUED: {variant: "secondary", label: "Đang chờ"},
  RUNNING: {variant: "info", label: "Đang xử lý"},
  COMPLETED: {variant: "success", label: "Hoàn tất"},
  FAILED: {variant: "danger", label: "Thất bại"},
};

const POLL_INTERVAL_MS = 2000;
const MAX_RUNNING_PER_USER = 5;

export default {
  page: {
    title: "Trung tâm xuất file",
    meta: [{name: "description", content: appConfig.description}],
  },
  data() {
    return {
      title: "Trung tâm xuất file",
      types: TYPES,
      granularities: GRANULARITIES,
      trangThaiOptions: TRANG_THAI_OPTIONS,
      maxRunning: MAX_RUNNING_PER_USER,
      form: this.defaultForm(),
      submitting: false,
      jobs: [],
      totalRows: 0,
      currentPage: 1,
      perPage: 20,
      pageOptions: [10, 20, 50, 100],
      filterStatus: "",
      filterType: "",
      pollTimer: null,
      loading: false,
    };
  },
  computed: {
    activeJobsCount() {
      return this.jobs.filter(j => j.status === "QUEUED" || j.status === "RUNNING").length;
    },
    selectedTypeLabel() {
      const t = this.types.find(x => x.value === this.form.type);
      return t ? t.label : "";
    },
    showCustomRange() {
      return this.form.granularity === "CUSTOM";
    },
    showAnchorDate() {
      return this.form.granularity && this.form.granularity !== "CUSTOM";
    },
    anchorDateLabel() {
      switch (this.form.granularity) {
        case "DAY": return "Ngày cần xuất";
        case "WEEK": return "Ngày bất kỳ trong tuần";
        case "MONTH": return "Ngày bất kỳ trong tháng";
        case "QUARTER": return "Ngày bất kỳ trong quý";
        case "YEAR": return "Ngày bất kỳ trong năm";
        default: return "Ngày";
      }
    },
  },
  created() {
    this.loadJobs();
    this.startPolling();
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    defaultForm() {
      const today = new Date().toISOString().slice(0, 10);
      return {
        type: "VAN_BAN_DI",
        granularity: "MONTH",
        fromDate: today,
        toDate: today,
        trangThaiCode: "",
        keyword: "",
      };
    },
    statusBadge(status) {
      return STATUS_VARIANTS[status] || {variant: "secondary", label: status || ""};
    },
    formatDate(dt) {
      if (!dt) return "";
      const d = new Date(dt);
      if (isNaN(d.getTime())) return dt;
      const pad = n => String(n).padStart(2, "0");
      return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },
    formatRange(job) {
      if (!job.fromDate && !job.toDate) return "Tất cả";
      const a = job.fromDate ? this.formatDateOnly(job.fromDate) : "...";
      const b = job.toDate ? this.formatDateOnly(job.toDate) : "...";
      return `${a} → ${b}`;
    },
    formatDateOnly(dt) {
      if (!dt) return "";
      const d = new Date(dt);
      if (isNaN(d.getTime())) return dt;
      const pad = n => String(n).padStart(2, "0");
      return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
    },
    formatFileSize(bytes) {
      if (!bytes) return "";
      const kb = bytes / 1024;
      if (kb < 1024) return `${kb.toFixed(1)} KB`;
      return `${(kb / 1024).toFixed(2)} MB`;
    },
    typeLabel(type) {
      const t = this.types.find(x => x.value === type);
      return t ? t.label : type;
    },
    async loadJobs() {
      this.loading = true;
      try {
        const params = {
          start: this.currentPage,
          limit: this.perPage,
          type: this.filterType || null,
          status: this.filterStatus || null,
        };
        const res = await this.$store.dispatch("exportStore/getPaging", params);
        if (res && res.resultCode === "SUCCESS") {
          this.jobs = (res.data && res.data.data) || [];
          this.totalRows = (res.data && res.data.totalRows) || 0;
        } else {
          this.$store.dispatch("snackBarStore/addNotify", notifyModel.addMessage(res));
        }
      } finally {
        this.loading = false;
      }
    },
    async refreshActive() {
      // Chỉ refresh khi có job đang chạy (giảm tải khi idle).
      if (this.activeJobsCount === 0) return;
      try {
        const params = {
          start: this.currentPage,
          limit: this.perPage,
          type: this.filterType || null,
          status: this.filterStatus || null,
        };
        const res = await this.$store.dispatch("exportStore/getPaging", params);
        if (res && res.resultCode === "SUCCESS") {
          this.jobs = (res.data && res.data.data) || [];
          this.totalRows = (res.data && res.data.totalRows) || 0;
        }
      } catch (e) {
        // ignore polling errors
      }
    },
    startPolling() {
      this.stopPolling();
      this.pollTimer = setInterval(() => this.refreshActive(), POLL_INTERVAL_MS);
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    pickTypeCard(type) {
      this.form.type = type;
    },
    async submit() {
      if (!this.form.type) {
        this.$store.dispatch("snackBarStore/addNotify", {
          resultCode: "NAME_EXIST",
          resultString: "Vui lòng chọn loại dữ liệu cần xuất.",
        });
        return;
      }
      this.submitting = true;
      try {
        const payload = {
          type: this.form.type,
          granularity: this.form.granularity,
          fromDate: this.form.fromDate || null,
          toDate: this.form.granularity === "CUSTOM" ? (this.form.toDate || null) : null,
          trangThaiCode: this.form.trangThaiCode || null,
          trangThaiName: this.trangThaiOptions.find(x => x.value === this.form.trangThaiCode)?.label,
          keyword: this.form.keyword || null,
        };
        const res = await this.$store.dispatch("exportStore/create", payload);
        if (res && res.resultCode === "SUCCESS") {
          this.$store.dispatch("snackBarStore/addNotify", {
            resultCode: "SUCCESS",
            resultString: res.resultString || "Đã đưa yêu cầu vào hàng đợi",
          });
          this.currentPage = 1;
          await this.loadJobs();
        } else {
          this.$store.dispatch("snackBarStore/addNotify", notifyModel.addMessage(res));
        }
      } finally {
        this.submitting = false;
      }
    },
    async confirmDelete(job) {
      const ok = await this.$swalConfirm("Xóa yêu cầu xuất", `Xóa yêu cầu "${job.fileName || this.typeLabel(job.type)}"?`);
      if (!ok) return;
      const res = await this.$store.dispatch("exportStore/delete", job.id);
      if (res && res.resultCode === "SUCCESS") {
        this.$store.dispatch("snackBarStore/addNotify", {
          resultCode: "SUCCESS",
          resultString: res.resultString || "Đã xóa yêu cầu xuất",
        });
        await this.loadJobs();
      } else {
        this.$store.dispatch("snackBarStore/addNotify", notifyModel.addMessage(res));
      }
    },
    async $swalConfirm(title, text) {
      if (!this.$swal) return window.confirm(text);
      const result = await this.$swal({
        title,
        text,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Xóa",
        cancelButtonText: "Hủy",
        confirmButtonColor: "#dc3545",
      });
      return result && result.isConfirmed;
    },
    downloadUrl(job) {
      const tokenRaw = localStorage.getItem("user-token");
      const token = tokenRaw ? JSON.parse(tokenRaw) : "";
      // Nếu đang ở production, dùng VUE_APP_API_URL (ví dụ apido.dthu.edu.vn);
      // khi phát triển (dev) giữ đường dẫn tương đối để devServer proxy hoạt động.
      if (process.env.NODE_ENV === 'production' && process.env.VUE_APP_API_URL) {
        const base = process.env.VUE_APP_API_URL;
        const normalized = base.endsWith('/') ? base : base + '/';
        return `${normalized}ExportJob/download/${job.id}?access_token=${encodeURIComponent(token)}`;
      }
      return `/api/v1/ExportJob/download/${job.id}?access_token=${encodeURIComponent(token)}`;
    },
    onPageChange(page) {
      this.currentPage = page;
      this.loadJobs();
    },
  },
};
</script>

<template>
  <div>
    <!-- Form tạo yêu cầu xuất -->
    <div class="row">
      <div class="col-12">
        <div class="card mb-2">
          <div class="card-body">
            <div class="row align-items-center mb-3">
              <div class="col-md-8">
                <h4 class="font-size-18 fw-bold text-dark mb-1">
                  <i class="mdi mdi-cloud-download-outline me-2 text-primary"></i>Trung tâm xuất file
                </h4>
                <small class="text-muted">
                  Chọn loại dữ liệu, khoảng thời gian rồi tạo yêu cầu. File sẽ được tạo ngầm — bạn có thể đóng/refresh trang, quay lại để tải về.
                </small>
              </div>
              <div class="col-md-4 text-md-end">
                <span v-if="activeJobsCount > 0" class="badge bg-info">
                  <i class="mdi mdi-progress-clock me-1"></i>{{ activeJobsCount }} yêu cầu đang xử lý
                </span>
                <span v-else class="badge bg-light text-muted">
                  <i class="mdi mdi-check-circle-outline me-1"></i>Không có yêu cầu đang chạy
                </span>
              </div>
            </div>

            <!-- Chọn loại dữ liệu -->
            <div class="row g-2 mb-3">
              <div v-for="t in types" :key="t.value" class="col-md-3 col-sm-6">
                <div
                    class="export-type-card"
                    :class="{ active: form.type === t.value }"
                    @click="pickTypeCard(t.value)"
                >
                  <i class="mdi me-2" :class="t.icon"></i>
                  <span>{{ t.label }}</span>
                </div>
              </div>
            </div>

            <div class="row g-3">
              <div class="col-md-3">
                <label class="form-label fw-bold">Chế độ thời gian</label>
                <select v-model="form.granularity" class="form-control form-select">
                  <option v-for="g in granularities" :key="g.value" :value="g.value">{{ g.label }}</option>
                </select>
              </div>
              <div v-if="showAnchorDate" class="col-md-3">
                <label class="form-label fw-bold">{{ anchorDateLabel }}</label>
                <input v-model="form.fromDate" type="date" class="form-control" />
              </div>
              <template v-if="showCustomRange">
                <div class="col-md-3">
                  <label class="form-label fw-bold">Từ ngày</label>
                  <input v-model="form.fromDate" type="date" class="form-control" />
                </div>
                <div class="col-md-3">
                  <label class="form-label fw-bold">Đến ngày</label>
                  <input v-model="form.toDate" type="date" class="form-control" />
                </div>
              </template>
              <div class="col-md-3">
                <label class="form-label fw-bold">Trạng thái</label>
                <select v-model="form.trangThaiCode" class="form-control form-select">
                  <option v-for="s in trangThaiOptions" :key="s.value" :value="s.value">{{ s.label }}</option>
                </select>
              </div>
              <div class="col-md-3">
                <label class="form-label fw-bold">Từ khóa (tùy chọn)</label>
                <input v-model="form.keyword" type="text" class="form-control" placeholder="Số ký hiệu / trích yếu..." />
              </div>
            </div>

            <div class="text-end mt-3">
              <small class="text-muted me-3">Tối đa {{ maxRunning }} yêu cầu đang chạy đồng thời.</small>
              <b-button variant="primary" :disabled="submitting" @click="submit">
                <i v-if="!submitting" class="mdi mdi-cloud-upload-outline me-1"></i>
                <b-spinner v-else small class="me-1"></b-spinner>
                Tạo yêu cầu xuất
              </b-button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Lịch sử / hàng đợi -->
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="row align-items-end mb-2">
              <div class="col-md-4">
                <h5 class="fw-bold mb-0"><i class="mdi mdi-history me-2"></i>Lịch sử xuất file</h5>
                <small class="text-muted">Tự động cập nhật mỗi 2 giây khi có yêu cầu đang chạy.</small>
              </div>
              <div class="col-md-3">
                <label class="form-label">Loại</label>
                <select v-model="filterType" class="form-control form-select" @change="loadJobs">
                  <option value="">Tất cả</option>
                  <option v-for="t in types" :key="t.value" :value="t.value">{{ t.label }}</option>
                </select>
              </div>
              <div class="col-md-3">
                <label class="form-label">Trạng thái</label>
                <select v-model="filterStatus" class="form-control form-select" @change="loadJobs">
                  <option value="">Tất cả</option>
                  <option value="QUEUED">Đang chờ</option>
                  <option value="RUNNING">Đang xử lý</option>
                  <option value="COMPLETED">Hoàn tất</option>
                  <option value="FAILED">Thất bại</option>
                </select>
              </div>
              <div class="col-md-2 text-end">
                <b-button variant="light" size="sm" @click="loadJobs">
                  <i class="mdi mdi-refresh"></i> Làm mới
                </b-button>
              </div>
            </div>

            <div class="table-responsive">
              <table class="table table-bordered table-hover align-middle mb-0">
                <thead class="table-light">
                  <tr>
                    <th style="width: 40px">#</th>
                    <th>Loại</th>
                    <th>Khoảng thời gian</th>
                    <th>Người tạo</th>
                    <th>Tạo lúc</th>
                    <th style="min-width: 240px">Trạng thái</th>
                    <th style="width: 80px">Số dòng</th>
                    <th style="width: 90px">Dung lượng</th>
                    <th style="width: 160px">Xử lý</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="jobs.length === 0">
                    <td colspan="9" class="text-center text-muted py-4">
                      <i class="mdi mdi-inbox-outline" style="font-size: 32px"></i>
                      <div>Chưa có yêu cầu xuất nào.</div>
                    </td>
                  </tr>
                  <tr v-for="(job, idx) in jobs" :key="job.id">
                    <td>{{ (currentPage - 1) * perPage + idx + 1 }}</td>
                    <td>
                      <span class="fw-bold">{{ job.typeName || typeLabel(job.type) }}</span>
                      <div v-if="job.trangThaiName" class="text-muted small">
                        Trạng thái: {{ job.trangThaiName }}
                      </div>
                      <div v-if="job.keyword" class="text-muted small">
                        Từ khóa: "{{ job.keyword }}"
                      </div>
                    </td>
                    <td>{{ formatRange(job) }}</td>
                    <td>{{ job.fullName || job.userName }}</td>
                    <td>{{ formatDate(job.createdAt) }}</td>
                    <td>
                      <div class="d-flex align-items-center mb-1">
                        <b-badge :variant="statusBadge(job.status).variant" class="me-2">
                          {{ statusBadge(job.status).label }}
                        </b-badge>
                        <small class="text-muted">{{ job.currentStep }}</small>
                      </div>
                      <b-progress
                          v-if="job.status === 'RUNNING' || job.status === 'QUEUED'"
                          :value="job.progress || 0"
                          :max="100"
                          show-progress
                          :variant="job.status === 'RUNNING' ? 'info' : 'secondary'"
                          striped
                          :animated="job.status === 'RUNNING'"
                          height="14px"
                      />
                      <div v-if="job.status === 'FAILED' && job.errorMessage"
                           class="text-danger small mt-1">
                        <i class="mdi mdi-alert-circle-outline me-1"></i>{{ job.errorMessage }}
                      </div>
                    </td>
                    <td class="text-end">{{ job.totalRows || 0 }}</td>
                    <td class="text-end">{{ formatFileSize(job.fileSize) }}</td>
                    <td>
                      <a
                          v-if="job.status === 'COMPLETED'"
                          :href="downloadUrl(job)"
                          class="btn btn-success btn-sm me-1"
                      >
                        <i class="mdi mdi-download"></i> Tải
                      </a>
                      <b-button
                          v-if="job.status !== 'RUNNING'"
                          variant="outline-danger"
                          size="sm"
                          @click="confirmDelete(job)"
                      >
                        <i class="mdi mdi-trash-can-outline"></i>
                      </b-button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-if="totalRows > perPage" class="d-flex justify-content-end mt-3">
              <b-pagination
                  v-model="currentPage"
                  :total-rows="totalRows"
                  :per-page="perPage"
                  @change="onPageChange"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.export-type-card {
  border: 2px solid #e2e7f1;
  border-radius: 10px;
  padding: 14px 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.15s ease;
  background: #fff;
}
.export-type-card:hover {
  border-color: #556ee6;
  background: #f6f8ff;
}
.export-type-card.active {
  border-color: #556ee6;
  background: #eef1ff;
  color: #556ee6;
  box-shadow: 0 2px 6px rgba(85, 110, 230, 0.18);
}
.export-type-card .mdi {
  font-size: 22px;
}
.form-label {
  margin-bottom: 4px;
}
</style>
